<template>
  <div class="weekly-overview">
    <!-- Week Navigation -->
    <div class="week-header">
      <button class="nav-btn" @click="previousWeek">&lt;</button>
      <div class="week-info">
        <span class="week-label">{{ weekLabel }}</span>
        <span class="week-range">{{ weekRange }}</span>
      </div>
      <button class="nav-btn" @click="nextWeek" :disabled="isCurrentWeek">&gt;</button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <span>Loading week...</span>
    </div>

    <template v-else>
      <!-- Week Grid -->
      <div class="week-grid">
        <div v-for="day in weekData" :key="day.date" class="day-card" :class="{
          'is-today': isToday(day.date),
          'has-activity': day.activity,
          [activityClass(day.activity)]: true
        }" @click="selectDay(day.date)">
          <!-- Day Header -->
          <div class="day-header">
            <span class="day-name">{{ day.day_short }}</span>
            <span class="day-date">{{ formatDayDate(day.date) }}</span>
          </div>

          <!-- Activity Badge -->
          <div v-if="day.activity" class="activity-badge" :class="activityClass(day.activity)">
            <span class="activity-icon">{{ activityIcon(day.activity) }}</span>
            <span class="activity-label">{{ day.activity.label }}</span>
          </div>

          <!-- Progress Circle -->
          <div class="day-progress">
            <div class="progress-ring" :style="progressStyle(day.completion?.overall || 0)">
              <span class="progress-value">{{ day.completion?.overall || 0 }}%</span>
            </div>
          </div>

          <!-- Macro Summary -->
          <div class="day-macros">
            <div class="macro-item carbs">
              <span class="macro-label">C</span>
              <span class="macro-value">{{ day.totals?.total_carbs || 0 }}g</span>
            </div>
            <div class="macro-item protein">
              <span class="macro-label">P</span>
              <span class="macro-value">{{ day.totals?.total_protein || 0 }}g</span>
            </div>
            <div class="macro-item fat">
              <span class="macro-label">F</span>
              <span class="macro-value">{{ day.totals?.total_fat || 0 }}g</span>
            </div>
          </div>

          <!-- Meal Count -->
          <div class="meal-count">
            <span class="count-value">{{ day.totals?.meal_count || 0 }}</span>
            <span class="count-label">meals</span>
          </div>
        </div>
      </div>

      <!-- Week Summary -->
      <div class="week-summary">
        <h3>Week Summary</h3>
        <div class="summary-grid">
          <div class="summary-card">
            <span class="summary-label">Avg. Completion</span>
            <span class="summary-value">{{ averageCompletion }}%</span>
          </div>
          <div class="summary-card">
            <span class="summary-label">Total Meals</span>
            <span class="summary-value">{{ totalMeals }}</span>
          </div>
          <div class="summary-card">
            <span class="summary-label">Matches</span>
            <span class="summary-value">{{ matchCount }}</span>
          </div>
          <div class="summary-card">
            <span class="summary-label">Training</span>
            <span class="summary-value">{{ trainingCount }}</span>
          </div>
        </div>
      </div>

      <!-- Legend -->
      <div class="legend">
        <div class="legend-item">
          <span class="legend-dot match"></span>
          <span>Match Day</span>
        </div>
        <div class="legend-item">
          <span class="legend-dot training"></span>
          <span>Training</span>
        </div>
        <div class="legend-item">
          <span class="legend-dot rest"></span>
          <span>Rest Day</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { Nutrition } from "@/services/nutrition-service";

export default {
  name: "WeeklyOverview",

  props: {
    userId: {
      type: [Number, String],
      required: true,
    },
    clubId: {
      type: String,
      default: null,
    },
    teamId: {
      type: [Number, String],
      default: null,
    },
    weight: {
      type: Number,
      default: null,
    },
  },

  emits: ["selectDay"],

  data() {
    return {
      loading: true,
      startDate: this.getMonday(new Date()),
      weekData: [],
    };
  },

  computed: {
    isCurrentWeek() {
      const currentMonday = this.getMonday(new Date());
      return this.startDate === currentMonday;
    },

    weekLabel() {
      if (this.isCurrentWeek) return "This Week";

      const today = new Date();
      const lastMonday = this.getMonday(new Date(today.setDate(today.getDate() - 7)));
      if (this.startDate === lastMonday) return "Last Week";

      return "Week of";
    },

    weekRange() {
      const start = new Date(this.startDate);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);

      const startStr = start.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
      const endStr = end.toLocaleDateString("en-GB", { day: "numeric", month: "short" });

      return `${startStr} - ${endStr}`;
    },

    averageCompletion() {
      if (this.weekData.length === 0) return 0;
      const total = this.weekData.reduce((sum, day) => sum + (day.completion?.overall || 0), 0);
      return Math.round(total / this.weekData.length);
    },

    totalMeals() {
      return this.weekData.reduce((sum, day) => sum + (day.totals?.meal_count || 0), 0);
    },

    matchCount() {
      return this.weekData.filter(day => day.activity?.type === "match").length;
    },

    trainingCount() {
      return this.weekData.filter(day => day.activity?.type === "training").length;
    },
  },

  watch: {
    startDate: {
      immediate: true,
      handler() {
        this.loadWeekData();
      },
    },
  },

  methods: {
    getMonday(date) {
      const d = new Date(date);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      d.setDate(diff);
      return d.toISOString().split("T")[0];
    },

    async loadWeekData() {
      this.loading = true;
      try {
        const response = await Nutrition.getWeeklyPlan(this.userId, this.startDate, {
          clubId: this.clubId,
          teamId: this.teamId,
          weight: this.weight,
        });

        if (response.data.success) {
          this.weekData = response.data.data.week;
        }
      } catch (error) {
        console.error("Error loading week data:", error);
        this.$toast.error("Failed to load week data");
      } finally {
        this.loading = false;
      }
    },

    previousWeek() {
      const date = new Date(this.startDate);
      date.setDate(date.getDate() - 7);
      this.startDate = date.toISOString().split("T")[0];
    },

    nextWeek() {
      if (this.isCurrentWeek) return;
      const date = new Date(this.startDate);
      date.setDate(date.getDate() + 7);
      this.startDate = date.toISOString().split("T")[0];
    },

    isToday(dateStr) {
      return dateStr === new Date().toISOString().split("T")[0];
    },

    formatDayDate(dateStr) {
      const date = new Date(dateStr);
      return date.getDate();
    },

    activityClass(activity) {
      if (!activity) return "rest";
      return activity.type || "rest";
    },

    activityIcon(activity) {
      if (!activity) return "";
      switch (activity.type) {
        case "match": return "M";
        case "training": return "T";
        default: return "";
      }
    },

    progressStyle(percent) {
      return {
        background: `conic-gradient(#28a745 ${percent}%, #e9ecef ${percent}%)`,
      };
    },

    selectDay(date) {
      this.$emit("selectDay", date);
    },
  },
};
</script>

<style scoped lang="scss">
.weekly-overview {
  max-width: 800px;
  margin: 0 auto;
}

.week-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 16px;
}

.nav-btn {
  width: 40px;
  border: none;
  background: #f0f0f0;
  border-radius: 8px;
  font-size: 1.2rem;
  cursor: pointer;
  color: #333;

  &:hover:not(:disabled) {
    background: #e0e0e0;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}

.week-info {
  text-align: center;
}

.week-label {
  display: block;
  font-weight: 700;
  font-size: 1.1rem;
  color: #333;
}

.week-range {
  font-size: 0.85rem;
  color: #666;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: #666;

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #f0f0f0;
    border-top-color: #007bff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 12px;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
}

.week-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  margin-bottom: 16px;
}

.day-card {
  background: white;
  border-radius: 10px;
  padding: 12px 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &.is-today {
    border-color: #007bff;
  }

  &.match {
    background: linear-gradient(to bottom, #fff5f5, white);
    border-color: #dc3545;
  }

  &.training {
    background: linear-gradient(to bottom, #f0fff4, white);
  }
}

.day-header {
  text-align: center;
}

.day-name {
  display: block;
  font-weight: 600;
  font-size: 0.75rem;
  color: #666;
  text-transform: uppercase;
}

.day-date {
  display: block;
  font-size: 1.1rem;
  font-weight: 700;
  color: #333;
}

.activity-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.65rem;

  &.match {
    background: #dc3545;
    color: white;
  }

  &.training {
    background: #28a745;
    color: white;
  }

  .activity-icon {
    font-size: 0.8em;
  }

  .activity-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 50px;
  }
}

.day-progress {
  margin: 4px 0;
}

.progress-ring {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    width: 32px;
    height: 32px;
    background: white;
    border-radius: 50%;
  }

  .progress-value {
    position: relative;
    font-size: 0.65rem;
    font-weight: 700;
    color: #333;
  }
}

.day-macros {
  display: flex;
  gap: 4px;
}

.macro-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 0.6rem;

  .macro-label {
    font-weight: 600;
  }

  .macro-value {
    color: #666;
  }

  &.carbs {
    .macro-label {
      color: #ff9800;
    }
  }

  &.protein {
    .macro-label {
      color: #dc3545;
    }
  }

  &.fat {
    .macro-label {
      color: #28a745;
    }
  }
}

.meal-count {
  display: flex;
  align-items: baseline;
  gap: 3px;
  font-size: 0.7rem;
  color: #666;

  .count-value {
    font-weight: 700;
    color: #333;
  }
}

.week-summary {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 16px;

  h3 {
    margin: 0 0 12px;
    font-size: 1rem;
    color: #333;
  }
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.summary-card {
  text-align: center;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;

  .summary-label {
    display: block;
    font-size: 0.7rem;
    color: #666;
    margin-bottom: 4px;
  }

  .summary-value {
    font-size: 1.2rem;
    font-weight: 700;
    color: #333;
  }
}

.legend {
  display: flex;
  justify-content: center;
  gap: 20px;
  padding: 12px;
  background: white;
  border-radius: 8px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: #666;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;

  &.match {
    background: #dc3545;
  }

  &.training {
    background: #28a745;
  }

  &.rest {
    background: #e9ecef;
  }
}

/* Mobile - horizontal scroll */
@media screen and (max-width: 600px) {
  .week-grid {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 8px;
    gap: 10px;
  }

  .day-card {
    flex: 0 0 85px;
    scroll-snap-align: start;
  }

  .summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .legend {
    flex-wrap: wrap;
    gap: 12px;
  }
}
</style>
